export function formatMoney(minor: number | undefined, currency = "USD", locale = "en-US"): string {
  if (minor === undefined) return "Not available";
  return new Intl.NumberFormat(locale, { style: "currency", currency }).format(minor / 100);
}

export function formatDateTime(value: string | undefined, timezone?: string): string {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timezone ? { timeZone: timezone } : {}),
  }).format(new Date(value));
}

export function formatDate(value: string | undefined): string {
  if (!value) return "Not available";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

export function formatPercent(basisPoints: number | undefined): string {
  if (basisPoints === undefined) return "Not available";
  return `${(basisPoints / 100).toFixed(basisPoints % 100 === 0 ? 0 : 1)}%`;
}

export function formatQuantity(milli: number | undefined): string {
  if (milli === undefined) return "Not available";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 }).format(milli / 1000);
}

/**
 * Title-cases a stored status or state name for display.
 *
 * The runtime guard is not defensive habit, it is a scar. A server response missing a field hands
 * this `undefined`, `.replaceAll` throws, and because nothing in the tree caught it React unmounted
 * the whole application rather than one pane: navigation included. The parameter stays typed
 * `string`, so a caller passing something genuinely wrong is still a compile error. This only
 * refuses to take the page down when the server breaks the contract at runtime, which is precisely
 * the moment a blank screen helps least.
 */
const AMERICAN_WORDS: Record<string, string> = {
  // Stored words whose American display spelling differs from the value the database
  // keeps. The wire value never changes; only the label a person reads does.
  cancelled: "canceled",
  organisation: "organization",
  enquiry: "inquiry",
  instalment: "installment",
};

export function titleCase(value: string): string {
  if (typeof value !== "string" || value === "") return "";
  return value
    .replaceAll("_", " ")
    .split(" ")
    .map((word) => AMERICAN_WORDS[word] ?? word)
    .join(" ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * The letter or two an avatar chip shows for a person: the first letter of the first and last
 * words of the name, so "Avery Morgan" reads AM and a single name still shows something.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return `${first}${last}`.toUpperCase() || "?";
}

export function localInputValue(date = new Date()): string {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
}

type ZonedParts = readonly [
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
];

function zonedParts(value: Date, timezone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const part = (type: Intl.DateTimeFormatPartTypes) => {
    const item = parts.find((candidate) => candidate.type === type);
    if (!item) throw new Error(`Could not resolve ${type} in ${timezone}.`);
    return Number(item.value);
  };
  return [part("year"), part("month"), part("day"), part("hour"), part("minute"), part("second")];
}

export function zonedDateTimeInput(value: string | undefined, timezone: string): string {
  if (!value) return "";
  const [year, month, day, hour, minute] = zonedParts(new Date(value), timezone);
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
}

export function zonedInputToISO(value: string, timezone: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) throw new Error("Enter a valid local date and time.");
  const expected = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  ] as const;
  const target = Date.UTC(expected[0], expected[1] - 1, expected[2], expected[3], expected[4], 0);
  let candidate = target;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const actual = zonedParts(new Date(candidate), timezone);
    const rendered = Date.UTC(actual[0], actual[1] - 1, actual[2], actual[3], actual[4], actual[5]);
    const correction = target - rendered;
    candidate += correction;
    if (correction === 0) break;
  }
  const actual = zonedParts(new Date(candidate), timezone);
  if (
    actual[0] !== expected[0] ||
    actual[1] !== expected[1] ||
    actual[2] !== expected[2] ||
    actual[3] !== expected[3] ||
    actual[4] !== expected[4]
  ) {
    throw new Error(
      `The selected local time does not exist in ${timezone}, usually because clocks change. Choose another time.`,
    );
  }
  return new Date(candidate).toISOString();
}
